import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ZoomIn, ZoomOut, Eye, EyeOff, Download,
  Layers, ChevronRight, AlertTriangle, Compass,
  Maximize2, Edit2, Send, Loader2, CheckCircle2,
  Plus, Trash2,
} from 'lucide-react';
import axios from 'axios';
import { API } from '../config';

// ─── Room type colors ──────────────────────────────────────────────
const ROOM_PALETTE = {
  'Bedroom':       { fill: 'rgba(99, 102, 241, 0.18)',  stroke: '#6366f1', label: '#818cf8' },
  'Living Room':   { fill: 'rgba(34, 197, 94, 0.15)',   stroke: '#22c55e', label: '#4ade80' },
  'Kitchen':       { fill: 'rgba(249, 115, 22, 0.15)',  stroke: '#f97316', label: '#fb923c' },
  'Bathroom':      { fill: 'rgba(6, 182, 212, 0.18)',   stroke: '#06b6d4', label: '#22d3ee' },
  'Toilet':        { fill: 'rgba(139, 92, 246, 0.18)',  stroke: '#8b5cf6', label: '#a78bfa' },
  'Balcony':       { fill: 'rgba(234, 179, 8, 0.15)',   stroke: '#eab308', label: '#facc15' },
  'Hall / Dining': { fill: 'rgba(236, 72, 153, 0.15)',  stroke: '#ec4899', label: '#f472b6' },
  'Hallway':       { fill: 'rgba(236, 72, 153, 0.12)',  stroke: '#ec4899', label: '#f472b6' },
  'Lobby':         { fill: 'rgba(168, 85, 247, 0.12)',  stroke: '#a855f7', label: '#c084fc' },
  'Store Room':    { fill: 'rgba(107, 114, 128, 0.18)', stroke: '#6b7280', label: '#9ca3af' },
  'Utility':       { fill: 'rgba(107, 114, 128, 0.15)', stroke: '#6b7280', label: '#9ca3af' },
  'WC':            { fill: 'rgba(139, 92, 246, 0.18)',  stroke: '#8b5cf6', label: '#a78bfa' },
  'Pooja Room':    { fill: 'rgba(234, 179, 8, 0.20)',   stroke: '#f59e0b', label: '#fbbf24' },
  'Corridor':      { fill: 'rgba(148, 163, 184, 0.12)', stroke: '#94a3b8', label: '#cbd5e1' },
  'Entrance':      { fill: 'rgba(52, 211, 153, 0.15)',  stroke: '#34d399', label: '#6ee7b7' },
};

const DEFAULT_ROOM_STYLE = { fill: 'rgba(148, 163, 184, 0.15)', stroke: '#94a3b8', label: '#cbd5e1' };

function getRoomStyle(type) {
  if (!type) return DEFAULT_ROOM_STYLE;
  for (const [key, style] of Object.entries(ROOM_PALETTE)) {
    if (type.toLowerCase().includes(key.toLowerCase())) return style;
  }
  return DEFAULT_ROOM_STYLE;
}

const ROOM_TYPE_OPTIONS = [
  'Bedroom', 'Master Bedroom', 'Living Room', 'Hall / Dining', 'Kitchen',
  'Kitchen / Dining', 'Bathroom', 'Toilet', 'Balcony', 'Corridor',
  'Entrance', 'Store Room', 'Pooja Room', 'Utility', 'Laundry', 'Office',
  'Stairs', 'Parking', 'Servant Room', 'Unknown Room',
];

const getPolygonBounds = (poly) => {
  if (!poly || poly.length === 0) return { x: 0, y: 0, width: 0, height: 0 };
  const xs = poly.map(p => p.x);
  const ys = poly.map(p => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

// ─── SmartEditor Component ─────────────────────────────────────────
const SmartEditor = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const labelInputRef = useRef(null);

  // State from navigation
  const {
    floorPlanBase64 = '',
    propertyType = 'home',
    orientation = 'north',
    detectedRooms = [],
    detectionMeta = {},
  } = location.state || {};

  // Canvas state
  const [image, setImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  // Room state initialized with polygon defaults if missing
  const [rooms, setRooms] = useState(() => {
    if (!detectedRooms || detectedRooms.length === 0) return [];
    return detectedRooms.map((r, idx) => {
      const id = r.id ?? (Date.now() + idx);
      if (!r.polygon || r.polygon.length < 3) {
        const rx = r.x ?? 40;
        const ry = r.y ?? 40;
        const rw = r.width ?? 15;
        const rh = r.height ?? 15;
        return {
          ...r,
          id,
          polygon: [
            { x: rx, y: ry },
            { x: rx + rw, y: ry },
            { x: rx + rw, y: ry + rh },
            { x: rx, y: ry + rh }
          ]
        };
      }
      return { ...r, id };
    });
  });

  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [hoveredRoomId, setHoveredRoomId] = useState(null);
  const [showOverlays, setShowOverlays] = useState(true);
  const [showLabels, setShowLabels] = useState(true);

  // Drag states
  const [draggedVertex, setDraggedVertex] = useState(null); // { roomId, index }
  const [draggedRoom, setDraggedRoom] = useState(null); // { roomId, startPt, originalPolygon }
  const [hoveredVertex, setHoveredVertex] = useState(null); // { roomId, index }

  // Inline label editing
  const [editingRoomId, setEditingRoomId] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  // Vastu analysis
  const [vastuLoading, setVastuLoading] = useState(false);
  const [vastuError, setVastuError] = useState('');

  const addRoom = () => {
    const size = 15;
    const x = 50 - size / 2;
    const y = 50 - size / 2;
    const newId = Date.now();
    const newRoom = {
      id: newId,
      type: 'Bedroom',
      detected_label: 'Bedroom',
      shape: 'polygon',
      confidence: 1.0,
      x: x,
      y: y,
      width: size,
      height: size,
      polygon: [
        { x: x, y: y },
        { x: x + size, y: y },
        { x: x + size, y: y + size },
        { x: x, y: y + size }
      ]
    };
    setRooms(prev => [...prev, newRoom]);
    setSelectedRoomId(newId);
  };

  const deleteRoom = (roomId) => {
    setRooms(prev => prev.filter(r => r.id !== roomId));
    if (selectedRoomId === roomId) {
      setSelectedRoomId(null);
    }
    setEditingRoomId(null);
  };

  // Load floor plan image
  useEffect(() => {
    if (!floorPlanBase64) return;
    const img = new Image();
    img.onload = () => {
      setImage(img);
      if (containerRef.current) {
        const cw = containerRef.current.clientWidth;
        const ch = containerRef.current.clientHeight;
        const scale = Math.min(cw / img.width, ch / img.height, 1) * 0.92;
        setZoom(scale);
        setPanOffset({
          x: (cw - img.width * scale) / 2,
          y: (ch - img.height * scale) / 2,
        });
      }
    };
    img.src = `data:image/png;base64,${floorPlanBase64}`;
  }, [floorPlanBase64]);

  // Focus label input when editing starts
  useEffect(() => {
    if (editingRoomId !== null && labelInputRef.current) {
      labelInputRef.current.focus();
      if (typeof labelInputRef.current.select === 'function') {
        labelInputRef.current.select();
      }
    }
  }, [editingRoomId]);

  // ─── Drawing ───────────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    const cw = canvas.parentElement.clientWidth;
    const ch = canvas.parentElement.clientHeight;
    canvas.width = cw * dpr;
    canvas.height = ch * dpr;
    canvas.style.width = `${cw}px`;
    canvas.style.height = `${ch}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Clear with dark background
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, cw, ch);

    // Draw subtle grid
    ctx.save();
    ctx.strokeStyle = 'rgba(51, 65, 85, 0.3)';
    ctx.lineWidth = 0.5;
    const gridSize = 40 * zoom;
    const startX = panOffset.x % gridSize;
    const startY = panOffset.y % gridSize;
    for (let x = startX; x < cw; x += gridSize) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, ch); ctx.stroke();
    }
    for (let y = startY; y < ch; y += gridSize) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(cw, y); ctx.stroke();
    }
    ctx.restore();

    // Transform for image + rooms
    ctx.save();
    ctx.translate(panOffset.x, panOffset.y);
    ctx.scale(zoom, zoom);

    // Draw floor plan image
    ctx.drawImage(image, 0, 0, image.width, image.height);

    // Draw room overlays
    if (showOverlays && rooms.length > 0) {
      const iw = image.width;
      const ih = image.height;

      rooms.forEach((room) => {
        const poly = room.polygon;
        if (!poly || poly.length < 3) return;

        const isSelected = room.id === selectedRoomId;
        const isHovered = room.id === hoveredRoomId;
        const style = getRoomStyle(room.type);

        const pts = poly.map(p => ({
          x: (p.x / 100) * iw,
          y: (p.y / 100) * ih,
        }));

        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        pts.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
        ctx.closePath();

        if (isSelected) {
          ctx.fillStyle = style.fill.replace(/[\d.]+\)$/, '0.38)');
        } else if (isHovered) {
          ctx.fillStyle = style.fill.replace(/[\d.]+\)$/, '0.28)');
        } else {
          ctx.fillStyle = style.fill;
        }
        ctx.fill();

        ctx.strokeStyle = style.stroke;
        ctx.lineWidth = isSelected ? 3 / zoom : isHovered ? 2.5 / zoom : 1.5 / zoom;
        if (isSelected) ctx.setLineDash([6 / zoom, 3 / zoom]);
        ctx.stroke();
        ctx.setLineDash([]);

        // Draggable vertex handles for selected room
        if (isSelected && showOverlays) {
          ctx.save();
          pts.forEach((pt, idx) => {
            const isVertexHovered = hoveredVertex && hoveredVertex.roomId === room.id && hoveredVertex.index === idx;
            ctx.beginPath();
            ctx.arc(pt.x, pt.y, (isVertexHovered ? 6 : 4.5) / zoom, 0, 2 * Math.PI);
            ctx.fillStyle = isVertexHovered ? '#6366f1' : '#ffffff';
            ctx.fill();
            ctx.strokeStyle = style.stroke;
            ctx.lineWidth = 1.5 / zoom;
            ctx.stroke();
          });
          ctx.restore();
        }

        if (showLabels) {
          const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
          const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
          const label = room.detected_label || room.type || 'Room';
          const fontSize = Math.max(10, Math.min(16, 14 / zoom));

          ctx.font = `bold ${fontSize}px Inter, system-ui, sans-serif`;
          const metrics = ctx.measureText(label);
          const tw = metrics.width;
          const th = fontSize;
          const padX = 6 / zoom;
          const padY = 3 / zoom;
          const radius = 4 / zoom;
          const lx = cx - tw / 2 - padX;
          const ly = cy - th / 2 - padY;
          const lw = tw + padX * 2;
          const lh = th + padY * 2;

          ctx.fillStyle = 'rgba(15, 23, 42, 0.88)';
          ctx.beginPath();
          ctx.roundRect(lx, ly, lw, lh, radius);
          ctx.fill();

          ctx.fillStyle = style.label;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(label, cx, cy);
        }
      });
    }

    ctx.restore();

    // Info overlay (bottom-left)
    ctx.fillStyle = 'rgba(15, 23, 42, 0.8)';
    ctx.fillRect(8, ch - 32, 200, 24);
    ctx.fillStyle = '#94a3b8';
    ctx.font = '11px Inter, system-ui, monospace';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(`Zoom: ${(zoom * 100).toFixed(0)}%  |  Rooms: ${rooms.length}`, 14, ch - 20);
  }, [image, zoom, panOffset, rooms, selectedRoomId, hoveredRoomId, showOverlays, showLabels, hoveredVertex]);

  useEffect(() => { draw(); }, [draw]);
  useEffect(() => {
    const handleResize = () => draw();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [draw]);

  // ─── Mouse Interactions ────────────────────────────────────────
  const getCanvasPoint = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left - panOffset.x) / zoom,
      y: (e.clientY - rect.top - panOffset.y) / zoom,
    };
  };

  const findRoomAtPoint = (pt) => {
    if (!image || !showOverlays) return null;
    const iw = image.width;
    const ih = image.height;
    for (let i = rooms.length - 1; i >= 0; i--) {
      const room = rooms[i];
      const poly = room.polygon;
      if (!poly || poly.length < 3) continue;
      const pts = poly.map(p => ({
        x: (p.x / 100) * iw,
        y: (p.y / 100) * ih,
      }));
      let inside = false;
      for (let j = 0, k = pts.length - 1; j < pts.length; k = j++) {
        const xi = pts[j].x, yi = pts[j].y;
        const xk = pts[k].x, yk = pts[k].y;
        if (((yi > pt.y) !== (yk > pt.y)) && (pt.x < (xk - xi) * (pt.y - yi) / (yk - yi) + xi)) {
          inside = !inside;
        }
      }
      if (inside) return room;
    }
    return null;
  };

  const handleMouseDown = (e) => {
    if (e.button === 1 || e.button === 2 || e.shiftKey) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
      e.preventDefault();
    } else {
      const pt = getCanvasPoint(e);

      // Check if click is on a vertex of the currently selected room
      if (selectedRoomId) {
        const selRoom = rooms.find(r => r.id === selectedRoomId);
        if (selRoom && selRoom.polygon && image) {
          const iw = image.width;
          const ih = image.height;
          const clickTolerance = Math.max(10, 8 / zoom);
          let clickedVertexIndex = -1;
          for (let i = 0; i < selRoom.polygon.length; i++) {
            const v = selRoom.polygon[i];
            const vx = (v.x / 100) * iw;
            const vy = (v.y / 100) * ih;
            if (Math.hypot(pt.x - vx, pt.y - vy) < clickTolerance) {
              clickedVertexIndex = i;
              break;
            }
          }
          if (clickedVertexIndex !== -1) {
            setDraggedVertex({ roomId: selectedRoomId, index: clickedVertexIndex });
            return;
          }
        }
      }

      // Check if click is inside any room polygon
      const room = findRoomAtPoint(pt);
      if (room) {
        setSelectedRoomId(room.id);
        setEditingRoomId(null);
        if (room.polygon) {
          setDraggedRoom({
            roomId: room.id,
            startPt: pt,
            originalPolygon: room.polygon.map(v => ({ ...v }))
          });
        }
      } else {
        setSelectedRoomId(null);
        setEditingRoomId(null);
      }
    }
  };

  const handleMouseMove = (e) => {
    if (isPanning) {
      setPanOffset({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
      return;
    }

    const pt = getCanvasPoint(e);

    // 1. If dragging a vertex
    if (draggedVertex && image) {
      const clampedX = Math.max(0, Math.min(image.width, pt.x));
      const clampedY = Math.max(0, Math.min(image.height, pt.y));
      const px = (clampedX / image.width) * 100;
      const py = (clampedY / image.height) * 100;

      setRooms(prev => prev.map(r => {
        if (r.id === draggedVertex.roomId) {
          const nextPoly = r.polygon.map((v, idx) => 
            idx === draggedVertex.index ? { x: px, y: py } : v
          );
          const bounds = getPolygonBounds(nextPoly);
          return {
            ...r,
            polygon: nextPoly,
            x: bounds.x,
            y: bounds.y,
            width: bounds.width,
            height: bounds.height
          };
        }
        return r;
      }));
      if (canvasRef.current) canvasRef.current.style.cursor = 'crosshair';
      return;
    }

    // 2. If dragging a room
    if (draggedRoom && image) {
      const dx = pt.x - draggedRoom.startPt.x;
      const dy = pt.y - draggedRoom.startPt.y;
      const pdx = (dx / image.width) * 100;
      const pdy = (dy / image.height) * 100;

      setRooms(prev => prev.map(r => {
        if (r.id === draggedRoom.roomId) {
          const nextPoly = draggedRoom.originalPolygon.map(v => ({
            x: Math.max(0, Math.min(100, v.x + pdx)),
            y: Math.max(0, Math.min(100, v.y + pdy))
          }));
          const bounds = getPolygonBounds(nextPoly);
          return {
            ...r,
            polygon: nextPoly,
            x: bounds.x,
            y: bounds.y,
            width: bounds.width,
            height: bounds.height
          };
        }
        return r;
      }));
      if (canvasRef.current) canvasRef.current.style.cursor = 'grabbing';
      return;
    }

    // 3. Normal hover state
    const room = findRoomAtPoint(pt);
    setHoveredRoomId(room ? room.id : null);

    // Check for hovered vertex to change cursor
    let foundVertex = null;
    if (selectedRoomId) {
      const selRoom = rooms.find(r => r.id === selectedRoomId);
      if (selRoom && selRoom.polygon && image) {
        const iw = image.width;
        const ih = image.height;
        const clickTolerance = Math.max(10, 8 / zoom);
        for (let i = 0; i < selRoom.polygon.length; i++) {
          const v = selRoom.polygon[i];
          const vx = (v.x / 100) * iw;
          const vy = (v.y / 100) * ih;
          if (Math.hypot(pt.x - vx, pt.y - vy) < clickTolerance) {
            foundVertex = { roomId: selectedRoomId, index: i };
            break;
          }
        }
      }
    }
    setHoveredVertex(foundVertex);

    if (canvasRef.current) {
      if (foundVertex) {
        canvasRef.current.style.cursor = 'crosshair';
      } else {
        canvasRef.current.style.cursor = room ? 'pointer' : 'grab';
      }
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
    setDraggedVertex(null);
    setDraggedRoom(null);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const delta = e.deltaY < 0 ? 1.1 : 0.9;
    const newZoom = Math.max(0.1, Math.min(5, zoom * delta));
    setPanOffset({
      x: mouseX - (mouseX - panOffset.x) * (newZoom / zoom),
      y: mouseY - (mouseY - panOffset.y) * (newZoom / zoom),
    });
    setZoom(newZoom);
  };

  // ─── Controls ──────────────────────────────────────────────────
  const handleZoomIn = () => setZoom(z => Math.min(5, z * 1.2));
  const handleZoomOut = () => setZoom(z => Math.max(0.1, z / 1.2));
  const handleFitToView = () => {
    if (!image || !containerRef.current) return;
    const cw = containerRef.current.clientWidth;
    const ch = containerRef.current.clientHeight;
    const scale = Math.min(cw / image.width, ch / image.height, 1) * 0.92;
    setZoom(scale);
    setPanOffset({
      x: (cw - image.width * scale) / 2,
      y: (ch - image.height * scale) / 2,
    });
  };

  const handleExport = () => {
    const data = {
      rooms: rooms.map(r => ({
        id: r.id, type: r.type, label: r.detected_label,
        polygon: r.polygon, x: r.x, y: r.y, width: r.width, height: r.height,
      })),
      orientation,
      propertyType,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'room_detection_result.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─── Inline label editing ─────────────────────────────────────
  const startEditLabel = (room) => {
    setEditingRoomId(room.id);
    setEditingValue(room.detected_label || room.type || '');
  };

  const commitLabel = () => {
    if (editingRoomId === null) return;
    const trimmed = editingValue.trim();
    if (trimmed) {
      setRooms(prev => prev.map(r =>
        r.id === editingRoomId
          ? { ...r, type: trimmed, detected_label: trimmed }
          : r
      ));
    }
    setEditingRoomId(null);
  };

  const handleLabelKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commitLabel(); }
    if (e.key === 'Escape') setEditingRoomId(null);
  };

  // ─── Vastu Analysis ────────────────────────────────────────────
  const handleVastuAnalysis = async () => {
    setVastuLoading(true);
    setVastuError('');
    try {
      const payload = {
        floor_plan_base64: floorPlanBase64,
        property_type: propertyType,
        orientation,
        detected_rooms: rooms.map(r => ({
          id: r.id,
          type: r.type,
          shape: r.shape || 'polygon',
          x: r.x ?? 0,
          y: r.y ?? 0,
          width: r.width ?? 10,
          height: r.height ?? 10,
          confidence: r.confidence ?? 0.85,
          vastu_zone: r.vastu_zone || 'CENTER',
          detected_label: r.detected_label || null,
          description: r.description || '',
          polygon: r.polygon || null,
        })),
      };
      const res = await axios.post(`${API}/vastu/upload`, payload, { withCredentials: true });
      const analysisId = res.data?.id;
      if (analysisId) {
        navigate(`/report/${analysisId}`);
      } else {
        setVastuError('Analysis saved but no ID returned.');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Vastu analysis failed.';
      setVastuError(msg);
    } finally {
      setVastuLoading(false);
    }
  };

  const selectedRoom = rooms.find(r => r.id === selectedRoomId);

  // Detection quality badge
  const identified = rooms.filter(r => r.type && r.type !== 'Unknown Room').length;
  const qualityColor = identified === rooms.length
    ? '#4ade80' : identified >= rooms.length / 2
    ? '#facc15' : '#f87171';

  // ─── No data guard ────────────────────────────────────────────
  if (!floorPlanBase64) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#0f172a', color: '#e2e8f0', fontFamily: 'Inter, system-ui, sans-serif'
      }}>
        <div style={{ textAlign: 'center', maxWidth: 420 }}>
          <AlertTriangle size={48} style={{ color: '#f59e0b', marginBottom: 16 }} />
          <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>No Floor Plan Data</h2>
          <p style={{ color: '#94a3b8', marginBottom: 24 }}>
            Please upload a floor plan through the Advanced Upload page first.
          </p>
          <button
            onClick={() => navigate('/advanced-upload')}
            style={{
              padding: '12px 32px', borderRadius: 12, border: 'none',
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              color: '#fff', fontWeight: 600, fontSize: 15, cursor: 'pointer'
            }}
          >
            Go to Upload
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', height: '100vh', background: '#0f172a',
      fontFamily: 'Inter, system-ui, sans-serif', color: '#e2e8f0', overflow: 'hidden'
    }}>
      {/* ─── Left Panel: Room List ─────────────────────────────── */}
      <div style={{
        width: 320, background: '#1e293b', borderRight: '1px solid #334155',
        display: 'flex', flexDirection: 'column', flexShrink: 0, overflowY: 'hidden'
      }}>
        {/* Panel Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid #334155',
          background: 'linear-gradient(180deg, #1e293b, #1a2332)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Layers size={18} style={{ color: '#6366f1' }} />
              <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.05em', color: '#6366f1', textTransform: 'uppercase' }}>
                Detected Rooms
              </span>
            </div>
            <button
              onClick={addRoom}
              title="Add New Room"
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '4px 8px', borderRadius: 6, border: 'none',
                background: 'rgba(99, 102, 241, 0.15)', color: '#818cf8',
                cursor: 'pointer', fontSize: 11, fontWeight: 600,
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(99, 102, 241, 0.25)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(99, 102, 241, 0.15)'; }}
            >
              <Plus size={12} /> Add
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <span style={{
              padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
              background: rooms.length > 0 ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
              color: rooms.length > 0 ? '#4ade80' : '#f87171',
              border: `1px solid ${rooms.length > 0 ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`
            }}>
              {rooms.length} rooms
            </span>
            <span style={{
              padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
              background: 'rgba(99,102,241,0.15)', color: '#818cf8',
              border: '1px solid rgba(99,102,241,0.3)'
            }}>
              {orientation.charAt(0).toUpperCase() + orientation.slice(1)}
            </span>
            {rooms.length > 0 && (
              <span style={{
                padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                background: 'rgba(15,23,42,0.6)', color: qualityColor,
                border: `1px solid ${qualityColor}40`
              }}>
                {identified}/{rooms.length} labeled
              </span>
            )}
          </div>
          {/* Detection warnings */}
          {detectionMeta?.warnings?.length > 0 && (
            <div style={{
              marginTop: 8, padding: '6px 10px', background: 'rgba(245,158,11,0.08)',
              borderRadius: 6, border: '1px solid rgba(245,158,11,0.2)'
            }}>
              <p style={{ fontSize: 10, color: '#fbbf24', margin: 0, lineHeight: 1.5 }}>
                {detectionMeta.warnings[0]}
              </p>
            </div>
          )}
        </div>

        {/* Room List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px' }}>
          {rooms.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
              <AlertTriangle size={32} style={{ marginBottom: 8, opacity: 0.5 }} />
              <p style={{ fontSize: 13 }}>No rooms detected</p>
            </div>
          ) : (
            rooms.map((room) => {
              const style = getRoomStyle(room.type);
              const isSelected = room.id === selectedRoomId;
              return (
                <div
                  key={room.id}
                  onClick={() => { setSelectedRoomId(isSelected ? null : room.id); setEditingRoomId(null); }}
                  onMouseEnter={() => setHoveredRoomId(room.id)}
                  onMouseLeave={() => setHoveredRoomId(null)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 12px', marginBottom: 4, borderRadius: 10,
                    cursor: 'pointer', transition: 'all 0.15s',
                    background: isSelected ? 'rgba(99,102,241,0.12)' : 'transparent',
                    border: isSelected ? '1px solid rgba(99,102,241,0.3)' : '1px solid transparent',
                  }}
                >
                  <div style={{
                    width: 10, height: 10, borderRadius: '50%',
                    background: style.stroke, flexShrink: 0,
                    boxShadow: `0 0 6px ${style.stroke}40`
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {room.detected_label || room.type || `Room ${room.id}`}
                    </div>
                    <div style={{ fontSize: 10, color: '#64748b', marginTop: 1 }}>
                      {room.confidence ? `${(room.confidence * 100).toFixed(0)}% conf` : ''}
                      {room.shape === 'polygon' ? ' · polygon' : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    <button
                      title="Delete Room"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteRoom(room.id);
                      }}
                      style={{
                        padding: 4, border: 'none', background: 'transparent',
                        color: '#64748b', cursor: 'pointer', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        borderRadius: 4, transition: 'all 0.15s'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#f87171'; e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = '#64748b'; e.currentTarget.style.background = 'transparent'; }}
                    >
                      <Trash2 size={13} />
                    </button>
                    <ChevronRight size={14} style={{ color: '#475569' }} />
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Selected Room Details */}
        {selectedRoom && (
          <div style={{
            padding: '14px 16px', borderTop: '1px solid #334155',
            background: 'rgba(15, 23, 42, 0.6)'
          }}>
            <h4 style={{ fontSize: 11, fontWeight: 700, color: '#818cf8', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Room Details
            </h4>

            {/* Editable label row */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Room Type</div>
              {editingRoomId === selectedRoom.id ? (
                <div style={{ display: 'flex', gap: 4 }}>
                  <select
                    ref={labelInputRef}
                    value={editingValue}
                    onChange={e => setEditingValue(e.target.value)}
                    onKeyDown={handleLabelKeyDown}
                    onBlur={commitLabel}
                    style={{
                      flex: 1, background: '#0f172a', border: '1px solid #6366f1',
                      borderRadius: 6, color: '#e2e8f0', fontSize: 12, padding: '4px 8px',
                      outline: 'none', cursor: 'pointer'
                    }}
                  >
                    {ROOM_TYPE_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <button
                    onClick={commitLabel}
                    style={{
                      padding: '4px 10px', borderRadius: 6, border: 'none',
                      background: '#6366f1', color: '#fff', fontSize: 11,
                      cursor: 'pointer', fontWeight: 600
                    }}
                  >
                    ✓
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => startEditLabel(selectedRoom)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '5px 8px', borderRadius: 6, cursor: 'pointer',
                    background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)',
                    transition: 'background 0.15s'
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#c7d2fe', flex: 1 }}>
                    {selectedRoom.detected_label || selectedRoom.type || 'Unknown'}
                  </span>
                  <Edit2 size={11} style={{ color: '#6366f1', flexShrink: 0 }} />
                </div>
              )}
            </div>

            <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.8 }}>
              <div><strong style={{ color: '#64748b' }}>Position:</strong> ({selectedRoom.x?.toFixed(1)}%, {selectedRoom.y?.toFixed(1)}%)</div>
              <div><strong style={{ color: '#64748b' }}>Size:</strong> {selectedRoom.width?.toFixed(1)}% × {selectedRoom.height?.toFixed(1)}%</div>
              <div><strong style={{ color: '#64748b' }}>Vertices:</strong> {selectedRoom.polygon?.length || 0}</div>
              <div><strong style={{ color: '#64748b' }}>Shape:</strong> {selectedRoom.shape || 'polygon'}</div>
              {selectedRoom.confidence > 0 && (
                <div><strong style={{ color: '#64748b' }}>Confidence:</strong> {(selectedRoom.confidence * 100).toFixed(0)}%</div>
              )}
            </div>
            <button
              onClick={() => deleteRoom(selectedRoom.id)}
              style={{
                width: '100%', marginTop: 12, padding: '6px 0', borderRadius: 8,
                border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.1)',
                color: '#f87171', fontWeight: 600, fontSize: 12, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.2)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; }}
            >
              <Trash2 size={12} /> Delete Room
            </button>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ padding: '12px 16px', borderTop: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {vastuError && (
            <div style={{
              padding: '6px 10px', borderRadius: 6, background: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.3)', fontSize: 11, color: '#f87171'
            }}>
              {vastuError}
            </div>
          )}
          <button
            id="vastu-analyze-btn"
            onClick={handleVastuAnalysis}
            disabled={vastuLoading || rooms.length === 0}
            style={{
              width: '100%', padding: '10px 0', borderRadius: 10, border: 'none',
              background: vastuLoading || rooms.length === 0
                ? 'rgba(99,102,241,0.3)'
                : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              color: '#fff', fontWeight: 600, fontSize: 13, cursor: vastuLoading ? 'wait' : rooms.length === 0 ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              transition: 'opacity 0.15s'
            }}
          >
            {vastuLoading
              ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Analyzing…</>
              : <><Send size={14} /> Send to Vastu Analysis</>}
          </button>
          <button
            id="export-rooms-btn"
            onClick={handleExport}
            style={{
              width: '100%', padding: '9px 0', borderRadius: 10,
              border: '1px solid #334155', background: 'transparent',
              color: '#94a3b8', fontWeight: 600, fontSize: 13, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
            }}
          >
            <Download size={14} /> Export Room JSON
          </button>
        </div>
      </div>

      {/* ─── Main Canvas Area ──────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Toolbar */}
        <div style={{
          height: 48, background: '#1e293b', borderBottom: '1px solid #334155',
          display: 'flex', alignItems: 'center', padding: '0 16px', gap: 4
        }}>
          <ToolbarButton icon={ZoomIn} tooltip="Zoom In (scroll up)" onClick={handleZoomIn} />
          <ToolbarButton icon={ZoomOut} tooltip="Zoom Out (scroll down)" onClick={handleZoomOut} />
          <ToolbarButton icon={Maximize2} tooltip="Fit to View" onClick={handleFitToView} />
          <div style={{ width: 1, height: 24, background: '#334155', margin: '0 8px' }} />
          <ToolbarButton
            icon={showOverlays ? Eye : EyeOff}
            tooltip={showOverlays ? 'Hide Overlays' : 'Show Overlays'}
            onClick={() => setShowOverlays(!showOverlays)}
            active={showOverlays}
          />
          <ToolbarButton
            icon={showOverlays ? Eye : EyeOff}
            tooltip={showLabels ? 'Hide Labels' : 'Show Labels'}
            onClick={() => setShowLabels(!showLabels)}
            active={showLabels}
            label="Labels"
          />
          <div style={{ width: 1, height: 24, background: '#334155', margin: '0 8px' }} />
          <ToolbarButton
            icon={Plus}
            tooltip="Add New Room"
            onClick={addRoom}
            label="Add Room"
          />
          <ToolbarButton
            icon={Trash2}
            tooltip={selectedRoomId ? "Delete Selected Room" : "Select a room to delete"}
            onClick={() => selectedRoomId && deleteRoom(selectedRoomId)}
            disabled={!selectedRoomId}
            label="Delete Room"
          />
          <div style={{ flex: 1 }} />
          {/* Detection quality indicator */}
          {rooms.length > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
              background: 'rgba(15,23,42,0.8)', border: `1px solid ${qualityColor}40`, color: qualityColor
            }}>
              <CheckCircle2 size={12} />
              {identified}/{rooms.length} labeled
            </div>
          )}
          <div style={{ width: 1, height: 24, background: '#334155', margin: '0 8px' }} />
          <div style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Compass size={14} />
            {orientation.charAt(0).toUpperCase() + orientation.slice(1)}-facing
            <span style={{ color: '#475569' }}>|</span>
            <span>{propertyType}</span>
          </div>
          <div style={{ width: 1, height: 24, background: '#334155', margin: '0 8px' }} />
          <button
            onClick={() => navigate('/advanced-upload')}
            style={{
              padding: '4px 12px', borderRadius: 6, border: '1px solid #334155',
              background: 'transparent', color: '#64748b', fontSize: 12, cursor: 'pointer'
            }}
          >
            ← New Upload
          </button>
        </div>

        {/* Canvas */}
        <div ref={containerRef} style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          <canvas
            ref={canvasRef}
            style={{ display: 'block', width: '100%', height: '100%' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
            onContextMenu={e => e.preventDefault()}
          />
          {/* Hint overlay */}
          {rooms.length === 0 && image && (
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)', textAlign: 'center',
              background: 'rgba(15,23,42,0.85)', padding: '24px 32px',
              borderRadius: 16, border: '1px solid #334155'
            }}>
              <AlertTriangle size={32} style={{ color: '#f59e0b', marginBottom: 12 }} />
              <p style={{ fontSize: 14, color: '#94a3b8', margin: 0 }}>
                No rooms detected in this floor plan.<br />
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  Try the Upload page with a clearer CAD-style image.
                </span>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Spin keyframe */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

// ─── Toolbar Button ──────────────────────────────────────────────
const ToolbarButton = ({ icon: Icon, tooltip, onClick, active, label, disabled }) => (
  <button
    onClick={onClick}
    title={tooltip}
    disabled={disabled}
    style={{
      display: 'flex', alignItems: 'center', gap: 4,
      padding: '6px 10px', borderRadius: 6, border: 'none',
      background: active ? 'rgba(99,102,241,0.15)' : 'transparent',
      color: disabled ? '#475569' : active ? '#818cf8' : '#94a3b8',
      cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 500,
      transition: 'all 0.15s',
      opacity: disabled ? 0.5 : 1,
    }}
  >
    <Icon size={16} />
    {label && <span>{label}</span>}
  </button>
);

export default SmartEditor;
